import { inject, Injectable } from '@angular/core'
import { FeathersService } from '../../feathers.service'
import { SmsMessage } from '../../../models/smsMessage'
import { Workgroup } from '../../../models/workgroup'
import { from, Observable, Subject } from 'rxjs'
import { first, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { PrivateMessageQueryType } from '../../../models/privateMessage'
import { UsersService } from '../users.service'

export type SmsQueryType = 'full' | 'paginatedList'

@Injectable({
  providedIn: 'root',
})
export class SmsService {
  private feathers = inject(FeathersService)
  private userService = inject(UsersService)

  private TAG = 'SmsService'

  private unreadCountForCurrentUser$$ = new Subject<number>()
  unreadCountForCurrentUser$ = this.unreadCountForCurrentUser$$.pipe(shareReplay({ bufferSize: 1, refCount: true }))

  constructor() {
    this.userService
      .getCurrentUserWorkgroup()
      .pipe(first())
      .subscribe((workgroup) => {
        this.updateUnreadCount(workgroup)
      })
  }

  send(sms: SmsMessage) {
    sms.contact = undefined
    sms.smsDirection = 'out'
    sms.smsStatus = 'pending'
    return this.feathers.createWithFactory(SmsMessage, sms)
  }

  getAllSms(): Observable<SmsMessage[]> {
    return this.feathers.find(SmsMessage).pipe(
      tap((messages: SmsMessage[]) => console.log('getAllSms', messages)),
      map((messages) => this.setReadByCurrentUser(messages)),
    )
  }

  getLatestOutboundSmsForWorkgroupPaginated(
    workgroup: Workgroup,
    queryType: SmsQueryType = 'full',
    limit: number = 20,
    skip: number = 0,
  ): Observable<SmsMessage[]> {
    return this.feathers.find(SmsMessage, {
      queryType,
      $limit: limit,
      $skip: skip,
      $sort: { updatedAt: -1 },
      $params_workgroupId: workgroup?.id,
      $params_smsDirection: 'out',
    })
  }

  getLatestInboundSmsForWorkgroupPaginated(
    workgroup: Workgroup,
    queryType: SmsQueryType = 'full',
    limit: number = 20,
    skip: number = 0,
  ): Observable<SmsMessage[]> {
    return this.feathers
      .find(SmsMessage, {
        queryType,
        $limit: limit,
        $skip: skip,
        $sort: { updatedAt: -1 },
        $params_workgroupId: workgroup?.id,
        $params_smsDirection: 'in',
      })
      .pipe(
        map((messages) => {
          this.updateUnreadCount(workgroup)
          return this.setReadByCurrentUser(messages)
        }),
      )
  }

  public getUnreadCountForCurrentUser(workgroup?: Workgroup): Observable<number> {
    return this.unreadCountForCurrentUser$
  }

  updateUnreadCount(workgroup?: Workgroup) {
    console.log(`${this.TAG}#updateUnreadCount`)
    this.getUnreadCountUpdateForCurrentUser(workgroup)
      .pipe(first())
      .subscribe((count) => this.unreadCountForCurrentUser$$.next(count))
  }

  private getUnreadCountUpdateForCurrentUser(workgroup?: Workgroup): Observable<number> {
    return this.userService.getCurrentUser().pipe(
      switchMap((user) =>
        from(
          this.feathers.count(SmsMessage.serviceName, {
            queryType: PrivateMessageQueryType.countUnread,
            $params_workgroupId: workgroup?.id,
          }),
        ),
      ),
      tap((count) => console.log('unreadCountUpdate', count)),
    )
  }

  async markRead(message: SmsMessage, workgroup?: Workgroup) {
    if (message.isRead()) return
    await this.feathers.create<any>('sms-read', { smsMessageId: message.id })
    message.setReadByCurrentUser(true)
    this.updateUnreadCount(workgroup)
  }

  private setReadByCurrentUser(messages: SmsMessage[]) {
    return messages.map((m) => m.setReadByCurrentUser(m.isRead()))
  }

  async markAllAsRead(workgroup?: Workgroup) {
    const currentUser = await this.userService.getCurrentUserPromise()

    await this.feathers.postCommand('msg-sms-markAllRead', {
      currentUserId: currentUser.id,
      workgroupId: workgroup?.id,
    })

    this.updateUnreadCount(workgroup)
  }
}
